import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/common.css';
import './CustomerManagementPage.css';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  gstNumber: '',
  panNumber: '',
  customerType: 'regular',
  notes: ''
};

const CustomerManagementPage = () => {
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({ name: '', customerType: '', city: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const navigate = useNavigate();

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to fetch customers');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const openAdd = () => {
    setEditId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (customer) => {
    setEditId(customer.id);
    setForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      gstNumber: customer.gstNumber || '',
      panNumber: customer.panNumber || '',
      customerType: customer.customerType || 'regular',
      notes: customer.notes || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(initialForm);
    setEditId(null);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editId) {
        await axios.put(`/api/customers/${editId}`, form);
        setSuccess('Customer updated successfully!');
      } else {
        await axios.post('/api/customers', form);
        setSuccess('Customer added successfully!');
      }

      setTimeout(() => {
        closeModal();
        fetchCustomers();
      }, 1500);
    } catch (err) {
      console.error('Save error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save customer';
      setError(errorMessage);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    setLoading(true);
    try {
      await axios.delete(`/api/customers/${id}`);
      setSuccess('Customer deleted successfully!');
      fetchCustomers();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete customer');
    }
    setLoading(false);
  };

  const filteredCustomers = customers.filter(customer => {
    const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
    const customerName = filters.name.toLowerCase();
    const customerType = customer.customerType || '';

    // Handle address which could be a string or object
    let customerCity = '';
    if (typeof customer.address === 'string') {
      customerCity = customer.address.toLowerCase();
    } else if (customer.address && typeof customer.address === 'object') {
      customerCity = (customer.address.city || '').toLowerCase();
    }

    const filterCity = filters.city.toLowerCase();

    return fullName.includes(customerName) &&
      (filters.customerType === '' || customerType === filters.customerType) &&
      customerCity.includes(filterCity);
  });

  const getCustomerTypeBadge = (type) => {
    const typeConfig = {
      regular: { label: 'Regular', class: 'badge-regular' },
      vip: { label: 'VIP', class: 'badge-vip' },
      wholesale: { label: 'Wholesale', class: 'badge-wholesale' }
    };

    const config = typeConfig[type] || typeConfig.regular;
    return <span className={`customer-type-badge ${config.class}`}>{config.label}</span>;
  };

  const handleViewBills = (customer) => {
    navigate('/billing/history', { state: { filterPhone: customer.phone } });
  };

  const handleNewBill = (customer) => {
    navigate('/billing/new', { state: { selectedCustomer: customer } });
  };


  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Customer Management</h2>
        <p>Manage your jewellery shop customers</p>
      </div>

      {/* Search and Filters */}
      <div className="card-container">
        <div className="filters-container">
          <div className="filter-group">
            <label>Search by Name:</label>
            <input
              type="text"
              value={filters.name}
              onChange={e => setFilters(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter customer name..."
              className="form-control"
            />
          </div>
          <div className="filter-group">
            <label>City:</label>
            <input
              type="text"
              value={filters.city}
              onChange={e => setFilters(prev => ({ ...prev, city: e.target.value }))}
              placeholder="Enter city..."
              className="form-control"
            />
          </div>
          <div className="filter-group">
            <label>Customer Type:</label>
            <select
              value={filters.customerType}
              onChange={e => setFilters(prev => ({ ...prev, customerType: e.target.value }))}
              className="form-select"
            >
              <option value="">All Types</option>
              <option value="regular">Regular</option>
              <option value="vip">VIP</option>
              <option value="wholesale">Wholesale</option>
            </select>
          </div>
          <div className="filter-group">
            <label>&nbsp;</label> {/* Spacer for alignment */}
            <button onClick={openAdd} className="btn-black">
              <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              Add New Customer
            </button>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="table-container">
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading customers...</p>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message">{success}</p>}

        {!loading && (
          <>
            {filteredCustomers.length === 0 ? (
              <div className="no-customers-container">
                <div className="no-customers-icon">
                  <svg className="customers-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.7 2.26A6.003 6.003 0 0 0 10 16v6h2v-6c0-2.21 1.79-4 4-4s4 1.79 4 4v6h2z" />
                  </svg>
                </div>
                <h3>No Customers Found</h3>
                <p>Start by adding your first customer to the system.</p>
                <button onClick={openAdd} className="btn-primary">
                  <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  Add Your First Customer
                </button>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Contact Info</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Documents</th>
                    <th>Quick Actions</th>
                    <th>Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map(customer => (
                    <tr key={customer.id} className="customer-row">
                      <td className="customer-name">
                        <div className="name-info">
                          <h4>{customer.firstName} {customer.lastName}</h4>
                          {customer.notes && (
                            <p className="customer-notes">{customer.notes}</p>
                          )}
                        </div>
                      </td>
                      <td className="contact-info">
                        <span className="contact-primary">{customer.phone || 'No Phone'}</span>
                        <span className="contact-secondary">{customer.email || 'No Email'}</span>
                      </td>
                      <td className="customer-type">
                        {getCustomerTypeBadge(customer.customerType)}
                      </td>
                      <td className="customer-address">
                        {(() => {
                          // Logic to extract City and Details
                          const addr = typeof customer.address === 'object' ? customer.address : { city: customer.address };
                          // If address is a string and simple, treat as city. If complex, might be tricky.
                          // Assuming simple city strings for now as per schema or object.
                          let city = 'Unknown City';
                          let details = '';

                          if (typeof customer.address === 'string') {
                            city = customer.address;
                          } else if (customer.address && typeof customer.address === 'object') {
                            city = customer.address.city || '';
                            details = [customer.address.street, customer.address.state, customer.address.zipCode].filter(Boolean).join(', ');
                          }

                          return (
                            <>
                              <span className="address-city">{city}</span>
                              {details && <span className="address-details">{details}</span>}
                            </>
                          );
                        })()}
                      </td>
                      <td className="customer-documents">
                        <div className="document-badges">
                          {(customer.gstNumber || customer.panNumber) ? (
                            <span className="badge-document badge-verified">Verified</span>
                          ) : (
                            <span className="badge-document badge-missing">Not Provided</span>
                          )}
                        </div>
                      </td>
                      <td className="customer-actions" style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button className="action-btn view-bills" title="View Bills" onClick={() => handleViewBills(customer)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <button className="action-btn new-bill" title="New Bill" onClick={() => handleNewBill(customer)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="customer-actions">
                        <button
                          onClick={() => openEdit(customer)}
                          className="action-btn edit"
                          title="Edit Customer"
                        >
                          <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(customer.id)}
                          className="action-btn delete"
                          title="Delete Customer"
                        >
                          <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Customer Modal */}
      {modalOpen && (
        <div className="modal-bg" onClick={closeModal}>
          <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editId ? 'Edit Customer' : 'Add New Customer'}</h3>
              <button className="close-btn" onClick={closeModal}>
                <svg className="close-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="customer-form">
              {error && <p className="error-message">{error}</p>}
              {success && <p className="success-message">{success}</p>}

              <div className="form-grid">
                <div className="form-group">
                  <label>First Name: *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleFormChange}
                    required
                    className="form-control"
                    placeholder="Enter first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name: *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleFormChange}
                    required
                    className="form-control"
                    placeholder="Enter last name"
                  />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter email address"
                  />
                </div>
                <div className="form-group">
                  <label>Phone: *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    required
                    className="form-control"
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Address:</label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter complete address"
                    rows="3"
                  />
                </div>
                <div className="form-group">
                  <label>GST Number:</label>
                  <input
                    type="text"
                    name="gstNumber"
                    value={form.gstNumber}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter GST number"
                  />
                </div>
                <div className="form-group">
                  <label>PAN Number:</label>
                  <input
                    type="text"
                    name="panNumber"
                    value={form.panNumber}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Enter PAN number"
                  />
                </div>
                <div className="form-group">
                  <label>Customer Type:</label>
                  <select
                    name="customerType"
                    value={form.customerType}
                    onChange={handleFormChange}
                    className="form-select"
                  >
                    <option value="regular">Regular</option>
                    <option value="vip">VIP</option>
                    <option value="wholesale">Wholesale</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Notes:</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleFormChange}
                    className="form-control"
                    placeholder="Additional notes about the customer..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (
                    <div className="loading-spinner-small"></div>
                  ) : (
                    editId ? 'Update Customer' : 'Add Customer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagementPage;